import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { Location } from 'vue-router'
import type { Store } from 'vuex'
import type { AppRouteConfig, DynamicFilterConfig } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { defineComponent } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import BrowseForm from '../../views/configurations/browse-configurations-form.view.vue'
import BrowseList from '../../views/configurations/browse-configurations-list.view.vue'
import BrowseRoot from '../../views/configurations/browse-configurations-root.view.vue'
import { ROUTER_CONSTS } from '../router.consts'
import { ENABLED_FILTER_CONFIG } from '../routes.filters'
import { ROUTES_PATHS } from '../routes.paths'

const NAMES = ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION]

const PATHS = Object.assign(ROUTES_PATHS, {
  root: 'configurations',
  [DomainEnum.BROWSE]: {
    attributeName: `:${ROUTER_CONSTS.params.attribute.name}`,
    attributeValue: `:${ROUTER_CONSTS.params.attribute.value}`,
  },
})

const featureId = FeatureIdEnum.CONFIGURATION

function getFilters(
  domain: DomainEnum.SEARCH | DomainEnum.BROWSE,
): AppRouteConfig['meta']['filters'] {
  return instance => {
    const toolConfig = instance.domains[domain]?.tools[featureId]
    const config = toolConfig?.pivotIds?.reduce<Record<string, DynamicFilterConfig>>(
      (result, pivotId) => ({
        ...result,
        [pivotId]: {
          enabled: true,
        },
      }),
      {},
    )
    return {
      config,
      ...(domain === DomainEnum.SEARCH
        ? {
            extra: {
              [ENABLED_FILTER_CONFIG.id]: ENABLED_FILTER_CONFIG,
            },
          }
        : {
            initiallyHidden: true,
          }),
    }
  }
}

const BROWSE_CONFIGURATIONS_ROUTES: Array<AppRouteConfig> = [
  {
    name: NAMES[DomainEnum.BROWSE].root,
    path: PATHS.root,
    component: defineComponent({
      beforeRouteEnter(to, from, next) {
        let redirectLocation: Location
        const store = CONTAINER.get<Store<unknown>>(TYPES.Store)
        const instance = store.getters[
          `${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`
        ] as Instance
        const toolConfig = instance.domains[DomainEnum.BROWSE]?.tools[featureId]
        const defaultAttributeName = toolConfig?.attributes[0]?.id
        if (defaultAttributeName) {
          redirectLocation = {
            ...to,
            name: NAMES[DomainEnum.BROWSE].attributes,
            params: {
              ...to.params,
              [ROUTER_CONSTS.params.attribute.name]: defaultAttributeName,
              [ROUTER_CONSTS.params.attribute.value]: '',
            },
            replace: true,
          }
        } else {
          redirectLocation = {
            ...to,
            name: ROUTER_CONSTS.routes.pageNotFound,
          }
        }
        redirectLocation.replace = true
        next(redirectLocation)
      },
    }),
    meta: {
      featureId,
    },
  },
  {
    name: NAMES[DomainEnum.BROWSE].attributes,
    path: `${PATHS.root}/${PATHS[DomainEnum.BROWSE].attributeName}`,
    component: BrowseRoot,
    meta: {
      featureId,
    },
    children: [
      {
        name: NAMES[DomainEnum.BROWSE].list,
        path: PATHS[DomainEnum.BROWSE].attributeValue,
        component: BrowseList,
        meta: {
          featureId,
          filters: getFilters(DomainEnum.BROWSE),
        },
      },
      {
        name: NAMES[DomainEnum.BROWSE].create,
        path: `${PATHS[DomainEnum.BROWSE].attributeValue}/${PATHS.actions.create}`,
        component: BrowseForm,
        meta: {
          featureId,
          parentRouteName: NAMES[DomainEnum.BROWSE].list,
        },
      },
      {
        name: NAMES[DomainEnum.BROWSE].update,
        path: `${PATHS[DomainEnum.BROWSE].attributeValue}/${PATHS.actions.update}`,
        component: BrowseForm,
        meta: {
          featureId,
          parentRouteName: NAMES[DomainEnum.BROWSE].list,
        },
      },
    ],
  },
]

export const CONFIGURATIONS_ROUTES = {
  [DomainEnum.BROWSE]: BROWSE_CONFIGURATIONS_ROUTES,
}
